import { useState } from 'react';
import { useReveal } from '@/hooks/useReveal';

export default function CTASection() {
  const { ref, visible } = useReveal();
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const name = (formData.get('name') as string || '').trim();
    const phone = (formData.get('phone') as string || '').trim();
    const email = (formData.get('email') as string || '').trim();
    const service = (formData.get('industry') as string || '').trim();
    const message = (formData.get('message') as string || '').trim();

    const errors: Record<string, string> = {};
    if (!name) errors.name = 'Please enter your full name';
    if (!phone) errors.phone = 'Please enter your phone number';
    if (!email) errors.email = 'Please enter your email address';
    if (!service) errors.industry = 'Please select a service';
    if (!message) errors.message = 'Please describe your project requirements';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});

    const plainMessage = `Hello Digi Flow,

I would like to enquire about your services.

-------------------------
Name: ${name}
Phone: ${phone}
Email: ${email}
Service Required: ${service}
Project Requirement: ${message}
-------------------------

Please contact me.`;

    const encodedMessage = encodeURIComponent(plainMessage);
    window.open(`https://wa.me/919746765488?text=${encodedMessage}`, '_blank');
  };

  return (
    <section id="cta" className="relative py-20 md:py-32 bg-secondary-900 overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-primary-500/10 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-accent-500/8 blur-3xl"></div>
      </div>

      {/* Pattern overlay */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)', backgroundSize: '48px 48px' }}>
      </div>

      <div ref={ref} className={`relative max-w-4xl mx-auto px-4 md:px-6 text-center reveal ${visible ? 'visible' : ''}`}>
        {/* Badge */}
        <div className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-accent-500/15 border border-accent-400/30 mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-accent-400"></span>
          <span className="text-sm font-semibold text-accent-300">FREE 7-Day Trial</span>
        </div>

        <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-white mb-4 leading-tight">
          Ready to{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-accent-400">
            Automate Your Business
          </span>
          ?
        </h2>

        <p className="text-base md:text-lg text-white/55 max-w-2xl mx-auto mb-2 leading-relaxed">
          Experience Digi Flow completely free for 7 days. No Credit Card Required. Setup in Minutes. Cancel Anytime.
        </p>
        <p className="text-sm md:text-base text-white/45 max-w-2xl mx-auto mb-10 leading-relaxed">
          Join 1000+ businesses that have transformed their WhatsApp communication into a revenue engine.
          Get started with a free personalized demo today.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col items-center gap-3 mb-4">
          <a
            href="https://automate.dgflow.in/signup"
            target="_blank"
            rel="noopener noreferrer"
            className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-semibold bg-primary-500 text-white hover:bg-primary-400 transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] whitespace-nowrap shadow-lg shadow-primary-500/30"
          >
            <i className="ri-rocket-line group-hover:-translate-y-px transition-transform"></i>
            Start FREE Trial
          </a>
          <span className="text-xs text-white/25 font-medium">or</span>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-12">
          <a
            href="#demo-form"
            className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-semibold border-2 border-white/20 text-white hover:bg-white/10 transition-all duration-300 whitespace-nowrap"
          >
            Book Your Free Demo
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
          </a>
          <a
            href="https://wa.me/919746765488?text=Hi,%0A%0AI%20want%20to%20know%20more%20about%20your%20services."
            target="_blank"
            rel="noopener noreferrer"
            className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-semibold border-2 border-white/15 text-white/70 hover:text-white hover:bg-white/10 transition-all duration-300 whitespace-nowrap"
          >
            <i className="ri-whatsapp-line text-xl text-primary-400"></i>
            Chat on WhatsApp
          </a>
        </div>

        {/* Demo Form */}
        <div id="demo-form" className="max-w-lg mx-auto bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8 text-left">
          <h3 className="text-xl font-bold font-heading text-white mb-1">Get Your Personalized Demo</h3>
          <p className="text-sm text-white/50 mb-6">Fill in your details and our team will reach out within 2 hours.</p>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-white/60 mb-1.5">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your full name"
                    className={`w-full px-4 py-3 rounded-xl bg-white/10 border text-white text-sm placeholder:text-white/30 focus:outline-none focus:bg-white/15 transition-all ${formErrors.name ? 'border-rose-400/60' : 'border-white/10 focus:border-primary-400/50'}`}
                  />
                  {formErrors.name && (
                    <p className="text-xs text-rose-400 mt-1.5">{formErrors.name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-white/60 mb-1.5">Business Name *</label>
                  <input
                    type="text"
                    name="business"
                    placeholder="Your business name"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-primary-400/50 focus:bg-white/15 transition-all"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-white/60 mb-1.5">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="you@company.com"
                    className={`w-full px-4 py-3 rounded-xl bg-white/10 border text-white text-sm placeholder:text-white/30 focus:outline-none focus:bg-white/15 transition-all ${formErrors.email ? 'border-rose-400/60' : 'border-white/10 focus:border-primary-400/50'}`}
                  />
                  {formErrors.email && (
                    <p className="text-xs text-rose-400 mt-1.5">{formErrors.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-white/60 mb-1.5">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="+91 98765 43210"
                    className={`w-full px-4 py-3 rounded-xl bg-white/10 border text-white text-sm placeholder:text-white/30 focus:outline-none focus:bg-white/15 transition-all ${formErrors.phone ? 'border-rose-400/60' : 'border-white/10 focus:border-primary-400/50'}`}
                  />
                  {formErrors.phone && (
                    <p className="text-xs text-rose-400 mt-1.5">{formErrors.phone}</p>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-white/60 mb-1.5">Service Required *</label>
                <select
                  name="industry"
                  className={`w-full px-4 py-3 rounded-xl bg-white/10 border text-white text-sm focus:outline-none focus:bg-white/15 transition-all appearance-none ${formErrors.industry ? 'border-rose-400/60' : 'border-white/10 focus:border-primary-400/50'}`}
                  style={{ backgroundImage: 'none' }}
                  defaultValue=""
                >
                  <option value="" className="text-foreground-900">Select your service</option>
                  <option value="retail" className="text-foreground-900">Retail</option>
                  <option value="healthcare" className="text-foreground-900">Healthcare</option>
                  <option value="education" className="text-foreground-900">Education</option>
                  <option value="real-estate" className="text-foreground-900">Real Estate</option>
                  <option value="automobile" className="text-foreground-900">Automobile</option>
                  <option value="restaurant" className="text-foreground-900">Restaurant / Cafe</option>
                  <option value="finance" className="text-foreground-900">Finance / Insurance</option>
                  <option value="manufacturing" className="text-foreground-900">Manufacturing</option>
                  <option value="wholesale" className="text-foreground-900">Wholesale / Distribution</option>
                  <option value="other" className="text-foreground-900">Other</option>
                </select>
                {formErrors.industry && (
                  <p className="text-xs text-rose-400 mt-1.5">{formErrors.industry}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-white/60 mb-1.5">Project Requirement *</label>
                <textarea
                  name="message"
                  rows={3}
                  maxLength={500}
                  placeholder="Tell us about your business needs..."
                  className={`w-full px-4 py-3 rounded-xl bg-white/10 border text-white text-sm placeholder:text-white/30 focus:outline-none focus:bg-white/15 transition-all resize-none ${formErrors.message ? 'border-rose-400/60' : 'border-white/10 focus:border-primary-400/50'}`}
                ></textarea>
                <div className="flex justify-between items-center mt-1">
                  {formErrors.message ? (
                    <p className="text-xs text-rose-400">{formErrors.message}</p>
                  ) : (
                    <span></span>
                  )}
                  <span className="text-xs text-white/30">Max 500 characters</span>
                </div>
              </div>
              <button
                type="submit"
                className="w-full py-3.5 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-400 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Send Message
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}